import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import RobotFace from "@/components/RobotFace";
import { Switch } from "@/components/ui/switch";
import * as L from "lucide-react";

function GoogleTasksSyncCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tasks/status");
      setStatus(data);
    } catch { /* non-blocking */ }
  };
  useEffect(() => { load(); }, []);

  if (!status) return null;
  if (!status.google_connected) return null;

  const needsReconnect = status.needs_reconnect;
  const enabled = status.sync_enabled;

  const toggle = async (v) => {
    setBusy(true);
    try {
      await api.patch("/tasks/sync", { enabled: v });
      toast.success(v ? "Syncing with Google Tasks" : "Sync off");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };

  const pull = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tasks/pull");
      toast.success(`Pulled ${data.pulled} · skipped ${data.skipped}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't pull");
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-2xl border p-4 ${needsReconnect ? "border-amber-200 bg-amber-50" : enabled ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10" : "border-[var(--rammy-border)] bg-[var(--rm-surface)]"}`} data-testid="gtasks-card">
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-xl bg-[var(--rm-surface)] shadow-sm flex items-center justify-center shrink-0">
          <L.Check size={16} className="text-[var(--rammy-cyan-dark)]"/>
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium">Sync with Google Tasks</div>
            {enabled && <span className="text-[9px] uppercase tracking-widest bg-[var(--rammy-cyan)] text-[var(--rammy-text)] px-1.5 py-0.5 rounded-full font-semibold">On</span>}
            {needsReconnect && <span className="text-[9px] uppercase tracking-widest bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-full font-semibold">Reconnect</span>}
          </div>
          <div className="text-xs text-[var(--rammy-text-sec)] mt-1 leading-relaxed">
            {needsReconnect
              ? "Google is connected but the Tasks permission is missing. Disconnect Google Calendar and reconnect to include Tasks."
              : enabled
              ? "New Rammy reminders are pushed to your primary Google Tasks list. Tap Pull to import Google Tasks not yet in Rammy."
              : "Two-way sync between Rammy reminders and Google Tasks. Off by default."}
          </div>
          <div className="mt-3 flex gap-2 flex-wrap items-center">
            {!needsReconnect && (
              <>
                <Switch checked={enabled} onCheckedChange={toggle} disabled={busy} data-testid="toggle-gtasks-sync"/>
                {enabled && (
                  <Button onClick={pull} disabled={busy} variant="ghost" className="btn-pill h-8 px-3 border border-[var(--rammy-border)] text-xs" data-testid="gtasks-pull">
                    {busy ? <L.Loader2 size={12} className="animate-spin"/> : "Pull from Google"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function RemindersScreen() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState("");
  const [repeat, setRepeat] = useState("none");

  const load = () => api.get("/reminders").then(r=>setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!title.trim() || !when) { toast.error("Add title and time"); return; }
    await api.post("/reminders", { title, when: new Date(when).toISOString(), repeat, note: "" });
    setTitle(""); setWhen(""); setRepeat("none"); load(); toast.success("Reminder set");
  };
  const toggle = async (id) => { await api.patch(`/reminders/${id}/toggle`); load(); };
  const del = async (id) => { await api.delete(`/reminders/${id}`); load(); };

  return (
    <div className="p-5 space-y-4" data-testid="screen-reminders">
      <GoogleTasksSyncCard/>
      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4 space-y-3">
        <Input placeholder="What should I remind you?" value={title} onChange={e=>setTitle(e.target.value)} className="rounded-xl" data-testid="rem-title"/>
        <div className="grid grid-cols-2 gap-2">
          <Input type="datetime-local" value={when} onChange={e=>setWhen(e.target.value)} className="rounded-xl" data-testid="rem-when"/>
          <Select value={repeat} onValueChange={setRepeat}>
            <SelectTrigger className="rounded-xl"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">One-off</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={add} className="btn-pill btn-primary w-full" data-testid="rem-add">Add reminder</Button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <div className="text-sm text-[var(--rammy-text-muted)] py-8 text-center">No reminders yet.</div>}
        {items.map(r => (
          <div key={r.id} className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4 flex items-center gap-3">
            <button onClick={()=>toggle(r.id)} className={`w-5 h-5 rounded-md border ${r.done ? "bg-[var(--rammy-cyan)] border-[var(--rammy-cyan)]" : "border-[var(--rammy-border)]"} flex items-center justify-center`} data-testid={`rem-toggle-${r.id}`}>
              {r.done && <L.Check size={12}/>}
            </button>
            <div className="flex-1">
              <div className={`font-medium ${r.done ? "line-through text-[var(--rammy-text-muted)]" : ""}`}>{r.title}</div>
              <div className="text-xs text-[var(--rammy-text-muted)] flex items-center gap-2">
                <L.Clock size={12}/> {new Date(r.when).toLocaleString(undefined,{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
                <span className="capitalize">· {r.repeat}</span>
              </div>
            </div>
            <button onClick={()=>del(r.id)} className="text-[var(--rammy-text-muted)] hover:text-red-500" data-testid={`rem-del-${r.id}`}><L.Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}
